import { useDepot } from '#~/depot/hooks.js';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import { PostHogManager } from '#~/posthog/manager.js';
import { getStudioSummaryById } from '#~/posthog/utils/summaries.js';
import { getActiveStudioId, subscribeActiveStudio } from '#~/analytics/active-studio.js';
import type { RoomRecord } from '@studio/models/room';
import type { User } from '@studio/models/user';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { shallow } from 'zustand/shallow';

type DepotSlice = [Record<string, RoomRecord>, User | undefined];

/**
 * Keeps the ACTIVE studio's PostHog group context and profile in sync.
 *
 * Scope is deliberately the single studio the session is inside, not every
 * studio the account can see. The previous form called
 * `manager.group('studio', …)` for EVERY room record whenever any of them
 * changed. Two measured defects followed (ENG-4401, CI runs 30847458980 /
 * 30849725283):
 *
 * 1. Delivery loss. On accounts with many studios (the shared e2e account
 *    holds 100+) the bulk loop emitted 100+ $groupidentify captures inside
 *    one render pass. In e2e mode (request_batching: false, manager.ts) each
 *    capture is its own keepalive fetch POST; the burst saturates Chromium's
 *    shared ~64KiB in-flight keepalive quota, so captures landing mid-burst —
 *    measured: the boot $pageview — fail with status 0 and sit in a retry
 *    queue that posthog-js never drains on teardown when batching is off
 *    (_handle_unload returns before retryQueue.unload()). Windows lost 55%
 *    of boot pageviews on staging 47358; macOS 27.5%.
 * 2. Attribution. group() also switches the SDK's active group context, so
 *    the last-iterated studio — an arbitrary map position — became
 *    $groups.studio on every subsequent event, not the studio the user is in.
 *
 * Identifying only the active studio fixes both: sends are bounded (one per
 * room entry or metadata change of that studio) and $groups.studio is the
 * studio whose room is mounted. Profiles for other studios update when a
 * session enters them — group metadata freshness follows usage instead of
 * being re-broadcast on every boot.
 *
 * Per rules: code-comments-required, reference-rules-in-comments
 * (ai.knowledge/global).
 */
export const useStudioGroupIdentify = (): void => {
  const [roomRecords, user]: DepotSlice = useDepot((µ) => [µ.roomRecords, µ.user], shallow);

  // Room entry/exit is signalled by the room route via setActiveStudio, not by
  // any depot field — subscribe to the module store directly so the identify
  // fires at the mount moment instead of piggybacking on unrelated updates.
  const activeStudioId = useSyncExternalStore(subscribeActiveStudio, getActiveStudioId);

  const activeSummary = useMemo(
    () => (activeStudioId === undefined ? null : getStudioSummaryById(roomRecords, activeStudioId)),
    [roomRecords, activeStudioId]
  );

  // Content signature so the effect fires once per (studio, metadata) state,
  // not once per depot update — room records stream in continuously during
  // boot and each patch re-runs the effect with an unchanged summary.
  const signature = useMemo(() => {
    if (!user || activeSummary === null) {
      return null;
    }
    return JSON.stringify(activeSummary);
  }, [activeSummary, user]);

  const lastSignature = useRef<string | null>(null);

  useEffect(() => {
    if (isElectronTrayWindow() || !user || signature === null || activeSummary === null) {
      if (!user) {
        // Logout resets the dedupe so the next login re-identifies once.
        lastSignature.current = null;
      }
      return;
    }

    if (signature === lastSignature.current) {
      return;
    }

    lastSignature.current = signature;

    const manager = PostHogManager.getInstance();
    manager.group('studio', activeSummary.id, {
      name: activeSummary.name,
      organizationId: activeSummary.organizationId,
      agentCount: activeSummary.agentCount
    });
  }, [signature, user, activeSummary]);
};
